// src/services/contentInteractionService.ts - FIXED VERSION
import axios from 'axios';
import { getAuthHeaders, getClientInfo } from '@/utils/authHelper';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:1337';

interface RecordViewParams {
    ipAddress?: string;
    userAgent?: string;
    deviceType?: string;
    duration?: number;
    sessionId?: string;
    referrer?: string;
}

interface RecordDownloadParams {
    fileType?: string;
    fileSize?: number;
    downloadMethod?: 'direct' | 'licensed' | 'free' | 'trial';
}

interface RecordShareParams {
    shareType: 'social_media' | 'email' | 'direct_link' | 'qr_code' | 'embed';
    platform?: string;
    shareData?: any;
}

interface SubmitRatingParams {
    rating: number;
    comment?: string;
    isAnonymous?: boolean;
}

class ContentInteractionService {
    // ✅ NEW: Get current user from JWT
    private async getCurrentUser() {
        try {
            const response = await axios.get(
                `${API_URL}/api/users/me`,
                { headers: getAuthHeaders() }
            );
            return response.data;
        } catch (error) {
            console.error('❌ Error getting current user:', error);
            return null;
        }
    }

    // Record view
    async recordView(contentId: string, additionalData?: RecordViewParams) {
        try {
            const clientInfo = getClientInfo();
            
            // Try to get current user (optional for views)
            const currentUser = await this.getCurrentUser();

            const payload = {
                data: {
                    content: contentId,
                    ...(currentUser?.id && { user: currentUser.id }), // ✅ Add user if logged in
                    viewedAt: new Date().toISOString(),
                    ipAddress: clientInfo.ipAddress,
                    userAgent: clientInfo.userAgent,
                    deviceType: clientInfo.deviceType,
                    isUnique: true,
                    sessionId: clientInfo.sessionId,
                    referrer: window.location.href,
                    ...additionalData
                }
            };

            const response = await axios.post(
                `${API_URL}/api/content-views`,
                payload,
                { headers: getAuthHeaders() }
            );

            return response.data;
        } catch (error: any) {
            console.error('❌ Error recording view:', error);
            throw new Error(error.response?.data?.message || 'Không thể ghi nhận lượt xem');
        }
    }

    // ✅ FIXED: Toggle like - now correctly filters by current user
    async toggleLike(contentId: string, likeType: 'like' | 'love' | 'bookmark' | 'favorite' = 'like') {
        try {
            // First, get current user ID from JWT
            const currentUser = await this.getCurrentUser();
            if (!currentUser || !currentUser.id) {
                throw new Error('Vui lòng đăng nhập để thích tác phẩm');
            }

            // console.log('👤 Current user ID:', currentUser.id);

            // ✅ Get current user's like ONLY (not all likes)
            const existingLike = await this.getUserLike(contentId);

            if (existingLike) {
                // Update existing like or remove it
                if (existingLike.likeType === likeType) {
                    // Same type - toggle off (remove)
                    // await axios.delete(
                    //     `${API_URL}/api/content-likes/${existingLike.documentId}`,
                    //     { headers: getAuthHeaders() }
                    // );
                    // Không xóa dữ liệu
                    await axios.put(
                        `${API_URL}/api/content-likes/${existingLike.documentId}`,
                        {
                            data: {
                                likedAt: new Date().toISOString(),
                                isActive: false
                            }
                        },
                        { headers: getAuthHeaders() }
                    );

                    console.log('✅ Like removed successfully');
                    return { isLiked: false, likeType: null };
                } else {
                    // Different type - update
                    const response = await axios.put(
                        `${API_URL}/api/content-likes/${existingLike.documentId}`,
                        {
                            data: {
                                likeType,
                                likedAt: new Date().toISOString(),
                                isActive: true
                            }
                        },
                        { headers: getAuthHeaders() }
                    );

                    console.log('✅ Like updated successfully');
                    return { isLiked: true, likeType, data: response.data };
                }
            } else {
                // ✅ Create new like - EXPLICITLY set user ID
                const response = await axios.post(
                    `${API_URL}/api/content-likes`,
                    {
                        data: {
                            content: contentId,
                            user: currentUser.id, // ✅ Explicitly set user ID
                            likeType,
                            likedAt: new Date().toISOString(),
                            isActive: true
                        }
                    },
                    { headers: getAuthHeaders() }
                );

                // console.log('✅ Like created successfully:', response.data);
                return { isLiked: true, likeType, data: response.data };
            }
        } catch (error: any) {
            console.error('❌ Error toggling like:', error);
            throw new Error(error.response?.data?.message || 'Không thể thực hiện hành động thích');
        }
    }

    // ✅ FIXED: Get user's like - now filters by BOTH content AND current user
    private async getUserLike(contentId: string) {
        try {
            // Get current user first
            const currentUser = await this.getCurrentUser();
            if (!currentUser || !currentUser.id) {
                return null;
            }

            // ✅ CRITICAL FIX: Filter by content AND current user
            const response = await axios.get(
                `${API_URL}/api/content-likes?` +
                `filters[content][id][$eq]=${contentId}&` +
                `filters[user][id][$eq]=${currentUser.id}&` + // ✅ Use actual user ID
                `filters[isActive][$eq]=true&` + // ✅ Only active likes
                `populate=*`,
                { headers: getAuthHeaders() }
            );

            const likes = response.data.data;
            
            // console.log('🔍 getUserLike response:', { 
            //     contentId,
            //     userId: currentUser.id,
            //     likesCount: likes.length,
            //     firstLike: likes[0] 
            // });

            if (likes.length > 0) {
                const like = likes[0];
                return {
                    id: like.id,
                    documentId: like.documentId,
                    likeType: like.likeType || like.attributes?.likeType,
                    isActive: like.isActive ?? like.attributes?.isActive ?? true
                };
            }
            
            return null;
        } catch (error) {
            console.warn('Could not fetch user like:', error);
            return null;
        }
    }

    // Record download
    async recordDownload(contentId: string, params?: RecordDownloadParams) {
        try {
            const clientInfo = getClientInfo();
            
            // Get current user ID
            const currentUser = await this.getCurrentUser();
            if (!currentUser || !currentUser.id) {
                throw new Error('Vui lòng đăng nhập để tải xuống');
            }

            const payload = {
                data: {
                    content: contentId,
                    user: currentUser.id, // ✅ Explicitly set user ID
                    downloadedAt: new Date().toISOString(),
                    ipAddress: clientInfo.ipAddress,
                    isSuccessful: true,
                    downloadMethod: params?.downloadMethod || 'direct',
                    fileType: params?.fileType,
                    fileSize: params?.fileSize
                }
            };

            const response = await axios.post(
                `${API_URL}/api/content-downloads`,
                payload,
                { headers: getAuthHeaders() }
            );

            console.log('✅ Download recorded successfully');
            return response.data;
        } catch (error: any) {
            console.error('❌ Error recording download:', error);
            throw new Error(error.response?.data?.message || 'Không thể ghi nhận lượt tải');
        }
    }

    // Record share
    async recordShare(contentId: string, params: RecordShareParams) {
        try {
            // Get current user ID (optional for shares)
            const currentUser = await this.getCurrentUser();

            const payload = {
                data: {
                    content: contentId,
                    ...(currentUser?.id && { user: currentUser.id }), // ✅ Add user if logged in
                    shareType: params.shareType,
                    platform: params.platform,
                    sharedAt: new Date().toISOString(),
                    shareData: params.shareData || {},
                    clickCount: 0,
                    isActive: true
                }
            };

            const response = await axios.post(
                `${API_URL}/api/content-shares`,
                payload,
                { headers: getAuthHeaders() }
            );

            console.log('✅ Share recorded successfully');
            return response.data;
        } catch (error: any) {
            console.error('❌ Error recording share:', error);
            throw new Error(error.response?.data?.message || 'Không thể ghi nhận lượt chia sẻ');
        }
    }

    // Submit rating
    async submitRating(contentId: string, params: SubmitRatingParams) {
        try {
            // Get current user ID
            const currentUser = await this.getCurrentUser();
            if (!currentUser || !currentUser.id) {
                throw new Error('Vui lòng đăng nhập để đánh giá');
            }

            // Check if user already rated this content
            const existingRating = await this.getUserRating(contentId);

            const payload = {
                data: {
                    content: contentId,
                    user: currentUser.id, // ✅ Explicitly set user ID
                    rating: params.rating,
                    comment: params.comment,
                    ratedAt: new Date().toISOString(),
                    isAnonymous: params.isAnonymous || false,
                    helpfulVotes: 0,
                    reportCount: 0
                }
            };

            let response;
            if (existingRating) {
                // Update existing rating
                response = await axios.put(
                    `${API_URL}/api/content-ratings/${existingRating.documentId}`,
                    payload,
                    { headers: getAuthHeaders() }
                );
                console.log('✅ Rating updated successfully');
            } else {
                // Create new rating
                response = await axios.post(
                    `${API_URL}/api/content-ratings`,
                    payload,
                    { headers: getAuthHeaders() }
                );
                console.log('✅ Rating submitted successfully');
            }

            return { rating: params.rating, data: response.data };
        } catch (error: any) {
            console.error('❌ Error submitting rating:', error);
            throw new Error(error.response?.data?.message || 'Không thể gửi đánh giá');
        }
    }

    // ✅ FIXED: Get user's rating - now filters by current user
    private async getUserRating(contentId: string) {
        try {
            // Get current user first
            const currentUser = await this.getCurrentUser();
            if (!currentUser || !currentUser.id) {
                return null;
            }

            // ✅ Filter by content AND current user
            const response = await axios.get(
                `${API_URL}/api/content-ratings?` +
                `filters[content][id][$eq]=${contentId}&` +
                `filters[user][id][$eq]=${currentUser.id}&` + // ✅ Use actual user ID
                `populate=*`,
                { headers: getAuthHeaders() }
            );

            const ratings = response.data.data;
            
            if (ratings.length > 0) {
                const rating = ratings[0];
                return {
                    id: rating.id,
                    documentId: rating.documentId,
                    rating: rating.rating || rating.attributes?.rating,
                    comment: rating.comment || rating.attributes?.comment
                };
            }
            
            return null;
        } catch (error) {
            console.warn('Could not fetch user rating:', error);
            return null;
        }
    }

    // Get content statistics
    async getContentStats(contentId: string) {
        try {
            const [views, likes, downloads, shares, ratings] = await Promise.all([
                this.getViewCount(contentId),
                this.getLikeCount(contentId),
                this.getDownloadCount(contentId),
                this.getShareCount(contentId),
                this.getRatingStats(contentId)
            ]);

            return {
                viewCount: views,
                likeCount: likes,
                downloadCount: downloads,
                shareCount: shares,
                averageRating: ratings.average,
                ratingCount: ratings.count
            };
        } catch (error: any) {
            console.error('❌ Error fetching content stats:', error);
            return {
                viewCount: 0,
                likeCount: 0,
                downloadCount: 0,
                shareCount: 0,
                averageRating: 0,
                ratingCount: 0
            };
        }
    }

    // Get user interactions for content
    async getUserInteractions(contentId: string) {
        try {
            // console.log('👤 Fetching user interactions for:', contentId);
            
            const [like, rating] = await Promise.all([
                this.getUserLike(contentId),
                this.getUserRating(contentId)
            ]);

            // console.log('👤 User interactions result:', { like, rating });

            const interactions = [];

            if (like) {
                interactions.push({
                    id: like.id,
                    type: 'like' as const,
                    contentId,
                    userId: 'current',
                    data: { 
                        isLiked: true, // ✅ Explicitly set isLiked
                        likeType: like.likeType,
                        isActive: like.isActive 
                    },
                    createdAt: new Date().toISOString()
                });
            }

            if (rating) {
                interactions.push({
                    id: rating.id,
                    type: 'rating' as const,
                    contentId,
                    userId: 'current',
                    data: { 
                        rating: rating.rating,
                        comment: rating.comment 
                    },
                    createdAt: new Date().toISOString()
                });
            }

            // console.log('✅ User interactions formatted:', interactions);
            return interactions;
        } catch (error: any) {
            console.error('❌ Error fetching user interactions:', error);
            return [];
        }
    }

    // Helper methods for individual stats
    private async getViewCount(contentId: string): Promise<number> {
        try {
            const response = await axios.get(
                `${API_URL}/api/content-views?filters[content][id][$eq]=${contentId}&pagination[pageSize]=1`,
                { headers: getAuthHeaders() }
            );
            return response.data.meta.pagination.total || 0;
        } catch (error) {
            return 0;
        }
    }

    private async getLikeCount(contentId: string): Promise<number> {
        try {
            const response = await axios.get(
                `${API_URL}/api/content-likes?filters[content][id][$eq]=${contentId}&filters[isActive][$eq]=true&pagination[pageSize]=1`,
                { headers: getAuthHeaders() }
            );
            return response.data.meta.pagination.total || 0;
        } catch (error) {
            return 0;
        }
    }

    private async getDownloadCount(contentId: string): Promise<number> {
        try {
            const response = await axios.get(
                `${API_URL}/api/content-downloads?filters[content][id][$eq]=${contentId}&filters[isSuccessful][$eq]=true&pagination[pageSize]=1`,
                { headers: getAuthHeaders() }
            );
            return response.data.meta.pagination.total || 0;
        } catch (error) {
            return 0;
        }
    }

    private async getShareCount(contentId: string): Promise<number> {
        try {
            const response = await axios.get(
                `${API_URL}/api/content-shares?filters[content][id][$eq]=${contentId}&filters[isActive][$eq]=true&pagination[pageSize]=1`,
                { headers: getAuthHeaders() }
            );
            return response.data.meta.pagination.total || 0;
        } catch (error) {
            return 0;
        }
    }

    private async getRatingStats(contentId: string): Promise<{ average: number, count: number }> {
        try {
            const response = await axios.get(
                `${API_URL}/api/content-ratings?filters[content][id][$eq]=${contentId}`,
                { headers: getAuthHeaders() }
            );

            const ratings = response.data.data;
            if (ratings.length === 0) {
                return { average: 0, count: 0 };
            }

            const sum = ratings.reduce((acc: number, rating: any) =>
                acc + rating.rating, 0
            );

            return {
                average: sum / ratings.length,
                count: ratings.length
            };
        } catch (error) {
            return { average: 0, count: 0 };
        }
    }
}

export default new ContentInteractionService();